const escapeSvgText = (value) => (
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
);

const getPackageDimensions = (packageType, pinCount) => {
  const half = pinCount / 2;

  const pitch = packageType === 'DIP' ? 36 : 28;
  const bodyWidth = packageType === 'DIP' ? 170 : 130;
  const pinTopInset = 58;
  const pinBottomInset = 58;
  const bodyHeight = Math.max(160, (half - 1) * pitch + pinTopInset + pinBottomInset);
  const legLength = packageType === 'DIP' ? 32 : 20;

  return {
    pitch,
    bodyWidth,
    bodyHeight,
    legLength,
    pinStartY: pinTopInset,
    canvasPadding: 90
  };
};

const getPinSideAndIndex = (pinNumber, pinCount) => {
  const half = pinCount / 2;

  if (pinNumber <= half) {
    return {
      side: 'left',
      rowIndex: pinNumber - 1
    };
  }

  return {
    side: 'right',
    rowIndex: pinCount - pinNumber
  };
};

export const getIcPinPosition = ({ pinNumber, pinCount, packageType = 'DIP' }) => {
  const dims = getPackageDimensions(packageType, pinCount);
  const bodyX = dims.canvasPadding + dims.legLength;
  const bodyY = 40;
  const sideInfo = getPinSideAndIndex(pinNumber, pinCount);

  const y = bodyY + dims.pinStartY + sideInfo.rowIndex * dims.pitch;

  const anchorInset = 3;

  if (sideInfo.side === 'left') {
    return {
        side: 'left',
        x: bodyX + anchorInset,
        y,
        legStartX: bodyX - dims.legLength,
        legEndX: bodyX,
        labelX: bodyX - dims.legLength - 12,
        labelY: y
    };
    }

   return {
    side: 'right',
    x: bodyX + dims.bodyWidth - anchorInset,
    y,
    legStartX: bodyX + dims.bodyWidth,
    legEndX: bodyX + dims.bodyWidth + dims.legLength,
    labelX: bodyX + dims.bodyWidth + dims.legLength + 12,
    labelY: y
   };
};

export const generateIcPinLayout = ({
  packageType = 'DIP',
  pinCount = 8,
  pins = []
}) => {
  const normalizedPinCount = Math.max(2, Number(pinCount) || 8);

  return Array.from({ length: normalizedPinCount }, (_, index) => {
    const pinNumber = index + 1;
    const pin = pins[index] || {};
    const pinName = pin.pin_name || pin.pinName || `PIN${pinNumber}`;
    const pos = getIcPinPosition({
      pinNumber,
      pinCount: normalizedPinCount,
      packageType
    });

    const markerX = pos.side === 'left'
      ? pos.legStartX
      : pos.legEndX;

    return {
      pin_number: pinNumber,
      pin_name: pinName,
      side: pos.side,
      anchor_x: markerX,
      anchor_y: pos.y,
      label_x: pos.labelX,
      label_y: pos.labelY
    };
  });
};

export const generateIcSvg = ({
  name = 'IC',
  packageType = 'DIP',
  pinCount = 8,
  markerStyle = 'notch-dot',
  labelMode = 'number-name',
  pins = []
}) => {
  const normalizedPinCount = Math.max(2, Number(pinCount) || 8);
  const dims = getPackageDimensions(packageType, normalizedPinCount);

  const bodyX = dims.canvasPadding + dims.legLength;
  const bodyY = 40;
  const canvasWidth = bodyX + dims.bodyWidth + dims.legLength + dims.canvasPadding;
  const canvasHeight = bodyY + dims.bodyHeight + 60;

  const pinLegElements = Array.from({ length: normalizedPinCount }, (_, index) => {
    const pinNumber = index + 1;

    const pos = getIcPinPosition({
      pinNumber,
      pinCount: normalizedPinCount,
      packageType
    });

    return `
      <line
        x1="${pos.legStartX}"
        y1="${pos.y}"
        x2="${pos.legEndX}"
        y2="${pos.y}"
        stroke="#475569"
        stroke-width="5"
        stroke-linecap="round"
      />
    `;
  }).join('');

  const pinCalloutElements = Array.from({ length: normalizedPinCount }, (_, index) => {
  const pinNumber = index + 1;
  const pin = pins[index] || {};
  const pinName = pin.pin_name || pin.pinName || `PIN${pinNumber}`;

  const labelText = (() => {
    if (labelMode === 'number') return String(pinNumber);
    if (labelMode === 'name') return escapeSvgText(pinName);
    if (labelMode === 'none') return '';
    return `${pinNumber} ${escapeSvgText(pinName)}`;
  })();

  const pos = getIcPinPosition({
    pinNumber,
    pinCount: normalizedPinCount,
    packageType
  });

  const isLeft = pos.side === 'left';
  const textAnchor = isLeft ? 'end' : 'start';

  const markerX = isLeft ? pos.legStartX : pos.legEndX;
  const markerY = pos.y;

  const bubbleWidth = Math.max(44, labelText.length * 7 + 18);
  const bubbleHeight = 24;
  const bubbleX = isLeft
    ? pos.labelX - bubbleWidth + 8
    : pos.labelX - 8;
  const bubbleY = pos.labelY - bubbleHeight / 2;

  const textX = isLeft
    ? bubbleX + bubbleWidth - 9
    : bubbleX + 9;

  return `
    <g
      class="ic-pin-callout"
      data-pin-number="${pinNumber}"
      data-pin-name="${escapeSvgText(pinName)}"
    >
    <circle
      cx="${markerX}"
      cy="${markerY}"
      r="12"
      fill="transparent"
      class="ic-pin-hitbox"
      data-pin-number="${pinNumber}"
      data-pin-name="${escapeSvgText(pinName)}"
    />

    <circle
      cx="${markerX}"
      cy="${markerY}"
      r="6"
      fill="#2c9fa4"
      class="ic-pin-marker"
      data-pin-number="${pinNumber}"
      data-pin-name="${escapeSvgText(pinName)}"
    />

      ${labelText ? `
        <rect
          x="${bubbleX}"
          y="${bubbleY}"
          width="${bubbleWidth}"
          height="${bubbleHeight}"
          rx="8"
          fill="#ffffff"
          stroke="#cbd5e1"
          stroke-width="1.5"
          class="ic-pin-bubble"
          data-pin-number="${pinNumber}"
          data-pin-name="${escapeSvgText(pinName)}"
        />

        <text
          x="${textX}"
          y="${pos.labelY + 5}"
          font-family="Arial, sans-serif"
          font-size="12"
          fill="#334155"
          text-anchor="${textAnchor}"
          class="ic-pin-label"
          data-pin-number="${pinNumber}"
          data-pin-name="${escapeSvgText(pinName)}"
        >${labelText}</text>
      ` : ''}
    </g>
  `;
}).join('');

  const notch = markerStyle.includes('notch')
    ? `
      <path
        d="M ${bodyX + dims.bodyWidth / 2 - 22} ${bodyY}
           Q ${bodyX + dims.bodyWidth / 2} ${bodyY + 24}
             ${bodyX + dims.bodyWidth / 2 + 22} ${bodyY}"
        fill="#f8fafc"
        stroke="#0f172a"
        stroke-width="2"
      />
    `
    : '';

  const dot = markerStyle.includes('dot')
    ? `
      <circle
        cx="${bodyX + 24}"
        cy="${bodyY + 24}"
        r="6"
        fill="#0f172a"
      />
    `
    : '';

  return `
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="${canvasWidth}"
      height="${canvasHeight}"
      viewBox="0 0 ${canvasWidth} ${canvasHeight}"
      data-micropinouts-generated="ic"
      data-package-type="${escapeSvgText(packageType)}"
      data-pin-count="${normalizedPinCount}"
    >
    <rect width="100%" height="100%" fill="#ffffff" />

    ${pinLegElements}

    <rect
      x="${bodyX}"
      y="${bodyY}"
      width="${dims.bodyWidth}"
      height="${dims.bodyHeight}"
      rx="10"
      fill="#111827"
      stroke="#0f172a"
      stroke-width="2"
    />

    ${notch}
    ${dot}

    ${pinCalloutElements}

      <text
        x="${bodyX + dims.bodyWidth / 2}"
        y="${bodyY + dims.bodyHeight / 2 - 4}"
        font-family="Arial, sans-serif"
        font-size="18"
        font-weight="700"
        fill="#ffffff"
        text-anchor="middle"
      >${escapeSvgText(name)}</text>

      <text
        x="${bodyX + dims.bodyWidth / 2}"
        y="${bodyY + dims.bodyHeight / 2 + 20}"
        font-family="Arial, sans-serif"
        font-size="12"
        fill="#cbd5e1"
        text-anchor="middle"
      >${escapeSvgText(packageType)}-${normalizedPinCount}</text>
    </svg>
  `.trim();
};
